import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DataTable } from '../../components/DataTable/DataTable';
import { AutoSize } from '../../charts/AutoSize';
import { ChartTooltip } from '../../charts/tooltip';
import { useCosts } from '../../hooks';
import { useTheme } from '../../theme';
import {
  CHART_LABELS,
  COST_CHARTS,
  COST_GRANULARITY,
  COST_GROUPINGS,
  COST_RANGES,
  DEFAULT_QUERY,
  GROUPING_LABELS,
  RANGE_LABELS,
  pctChange,
  usd,
  type CostChart,
  type CostGrouping,
  type CostQuery,
  type CostSeries,
  type CostView,
} from '../../costs/model';
import { COST_FINDINGS } from '../../costs/mockData';
import { allViews, readLast, readSavedViews, writeLast, writeSavedViews } from '../../costs/views';
import styles from './Costs.module.scss';

const GRANULARITY_LABELS = { daily: 'Daily', monthly: 'Monthly' } as const;

const formatBucket = (value: string): string => {
  if (value.length === 7) {
    const [year, month] = value.split('-');
    return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString('en-GB', {
      month: 'short',
      year: '2-digit',
    });
  }
  const date = new Date(`${value}T00:00:00Z`);
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
};

const Delta = ({ current, previous }: { current: number; previous: number }) => {
  const change = pctChange(current, previous);
  if (change === null) {
    return <span className={styles.deltaFlat}>new</span>;
  }
  const up = change > 0.5;
  const down = change < -0.5;
  return (
    <span className={up ? styles.deltaUp : down ? styles.deltaDown : styles.deltaFlat}>
      {up ? '▲' : down ? '▼' : '•'} {Math.abs(change).toFixed(1)}%
    </span>
  );
};

export const Costs = () => {
  const last = useMemo(() => readLast(), []);
  const [query, setQuery] = useState<CostQuery>(last?.query ?? DEFAULT_QUERY);
  const [chart, setChart] = useState<CostChart>(last?.chart ?? 'stacked');
  const [savedViews, setSavedViews] = useState<CostView[]>(() => readSavedViews());
  const [viewName, setViewName] = useState('');
  const [savingView, setSavingView] = useState(false);
  const { data, isLoading, isFetching, error } = useCosts(query);
  const { theme } = useTheme();
  const { ink } = theme;

  useEffect(() => writeLast({ query, chart }), [query, chart]);

  const set = <K extends keyof CostQuery>(key: K, value: CostQuery[K]) =>
    setQuery((current) => ({ ...current, [key]: value }));
  const toggleFilter = (dimension: CostGrouping, value: string) =>
    setQuery((current) => {
      const existing = current.filters[dimension] ?? [];
      const next = existing.includes(value)
        ? existing.filter((item) => item !== value)
        : [...existing, value];
      return { ...current, filters: { ...current.filters, [dimension]: next } };
    });
  const drillInto = (key: string) => {
    if (key === 'other') {
      return;
    }
    setQuery((current) => {
      const nextGroup =
        COST_GROUPINGS.find(
          (dimension) =>
            dimension !== current.group_by && !(current.filters[dimension] ?? []).length,
        ) ?? current.group_by;
      return {
        ...current,
        filters: { ...current.filters, [current.group_by]: [key] },
        group_by: nextGroup,
      };
    });
  };
  const clearFilters = () => set('filters', {});
  const applyView = (view: CostView) => {
    setQuery(view.query);
    setChart(view.chart);
  };
  const saveView = () => {
    const name = viewName.trim();
    if (!name) {
      return;
    }
    const next = [
      ...savedViews.filter((view) => view.name !== name),
      { id: `view_${Date.now().toString(36)}`, name, query, chart },
    ];
    setSavedViews(next);
    writeSavedViews(next);
    setViewName('');
    setSavingView(false);
  };
  const deleteView = (id: string) => {
    const next = savedViews.filter((view) => view.id !== id);
    setSavedViews(next);
    writeSavedViews(next);
  };

  const activeFilters = (
    Object.entries(query.filters) as [CostGrouping, string[] | undefined][]
  ).filter(([, values]) => values && values.length > 0);

  const chartData = useMemo(
    () =>
      (data?.dates ?? []).map((date, index) => {
        const row: Record<string, number | string> = { date };
        data?.series.forEach((series) => {
          row[series.key] = Math.round(series.points[index].amount * 100) / 100;
        });
        return row;
      }),
    [data],
  );

  const columns = useMemo<ColumnDef<CostSeries, any>[]>(
    () => [
      {
        header: GROUPING_LABELS[query.group_by],
        accessorKey: 'label',
        size: 260,
        cell: ({ row }) => (
          <span className={styles.seriesCell}>
            <span
              className={styles.swatch}
              style={{ background: ink.series[Math.min(row.index, ink.series.length - 1)] }}
            />
            {row.original.label}
          </span>
        ),
      },
      {
        header: 'Total',
        accessorKey: 'total',
        size: 130,
        meta: { align: 'right' },
        cell: ({ getValue }) => usd(getValue<number>(), query.per_venue ? 2 : 0),
      },
      {
        header: 'Share',
        id: 'share',
        accessorFn: (row) => (data && data.total > 0 ? row.total / data.total : 0),
        size: 110,
        meta: { align: 'right' },
        cell: ({ getValue }) => `${(getValue<number>() * 100).toFixed(1)}%`,
      },
      {
        header: 'Previous',
        accessorKey: 'previous_total',
        size: 130,
        meta: { align: 'right' },
        cell: ({ getValue }) => usd(getValue<number>(), query.per_venue ? 2 : 0),
      },
      {
        header: 'Change',
        id: 'change',
        accessorFn: (row) => pctChange(row.total, row.previous_total) ?? 0,
        size: 110,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <Delta current={row.original.total} previous={row.original.previous_total} />
        ),
      },
    ],
    [query.group_by, query.per_venue, data, ink.series],
  );

  const total = data?.total ?? 0;
  const previous = data?.previous_total ?? 0;
  const perVenueNow = data && data.venues > 0 ? (query.per_venue ? total : total / data.venues) : 0;
  const periodMonths = query.range === '30d' ? 1 : Number(query.range.replace('m', ''));
  const findings = COST_FINDINGS.filter((finding) => {
    const accounts = query.filters.account ?? [];
    const services = query.filters.service ?? [];
    return (
      (accounts.length === 0 || accounts.includes(finding.account)) &&
      (services.length === 0 || services.includes(finding.service))
    );
  });

  const renderChart = (width: number, height: number) => {
    const common = {
      width,
      height,
      data: chartData,
      margin: { top: 12, right: 12, bottom: 0, left: 8 },
    };
    const axis = {
      tick: { fill: ink.secondary, fontSize: 11 },
      axisLine: false,
      tickLine: false,
    } as const;
    const seriesEls = (kind: 'bar' | 'line' | 'area') =>
      (data?.series ?? []).map((series, index) => {
        const colour =
          series.key === 'other'
            ? ink.secondary
            : ink.series[Math.min(index, ink.series.length - 1)];
        if (kind === 'bar') {
          return (
            <Bar
              key={series.key}
              isAnimationActive
              animationDuration={500}
              dataKey={series.key}
              name={series.label}
              stackId="cost"
              fill={colour}
              stroke={ink.surface}
              strokeWidth={1}
              maxBarSize={48}
              onClick={() => drillInto(series.key)}
              cursor="pointer"
            />
          );
        }
        if (kind === 'area') {
          return (
            <Area
              key={series.key}
              isAnimationActive
              animationDuration={500}
              type="monotone"
              dataKey={series.key}
              name={series.label}
              stackId="cost"
              stroke={colour}
              fill={colour}
              fillOpacity={0.35}
              strokeWidth={1.5}
            />
          );
        }
        return (
          <Line
            key={series.key}
            isAnimationActive
            animationDuration={500}
            type="monotone"
            dataKey={series.key}
            name={series.label}
            stroke={colour}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, stroke: ink.surface, strokeWidth: 2 }}
          />
        );
      });
    const shared = (
      <>
        <CartesianGrid vertical={false} stroke={ink.grid} />
        <XAxis dataKey="date" {...axis} tickFormatter={formatBucket} minTickGap={20} />
        <YAxis
          {...axis}
          tickFormatter={(value) =>
            query.per_venue
              ? usd(Number(value), 2)
              : `$${
                  Number(value) >= 1000 ? `${(Number(value) / 1000).toFixed(1)}k` : Number(value)
                }`
          }
        />
        <Tooltip
          cursor={{ fill: ink.grid }}
          content={<ChartTooltip format={(value) => usd(value, query.per_venue ? 2 : 0)} />}
        />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 12, color: ink.secondary }}
        />
      </>
    );
    if (chart === 'lines') {
      return (
        <LineChart {...common}>
          {shared}
          {seriesEls('line')}
        </LineChart>
      );
    }
    if (chart === 'area') {
      return (
        <AreaChart {...common}>
          {shared}
          {seriesEls('area')}
        </AreaChart>
      );
    }
    return (
      <BarChart {...common} barCategoryGap={query.granularity === 'daily' ? 2 : 14}>
        {shared}
        {seriesEls('bar')}
      </BarChart>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Costs</h1>
          <p className={styles.subtitle}>
            AWS spend across the organisation, USD, unblended. Click a bar or a row to drill in.
          </p>
        </div>
        <div className={styles.views}>
          {allViews().map((view) => (
            <span key={view.id} className={styles.viewChip}>
              <button type="button" className={styles.viewButton} onClick={() => applyView(view)}>
                {view.name}
              </button>
              {!view.builtin ? (
                <button
                  type="button"
                  className={styles.viewDelete}
                  onClick={() => deleteView(view.id)}
                  aria-label={`Delete view ${view.name}`}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
          {savingView ? (
            <form
              className={styles.saveForm}
              onSubmit={(event) => {
                event.preventDefault();
                saveView();
              }}
            >
              <input
                autoFocus
                value={viewName}
                onChange={(event) => setViewName(event.target.value)}
                placeholder="View name"
                aria-label="View name"
              />
              <button type="submit" className={styles.primarySmall}>
                Save
              </button>
              <button
                type="button"
                className={styles.ghostSmall}
                onClick={() => setSavingView(false)}
              >
                Cancel
              </button>
            </form>
          ) : (
            <button type="button" className={styles.ghostSmall} onClick={() => setSavingView(true)}>
              + Save current view
            </button>
          )}
        </div>
      </header>

      <div className={styles.controls}>
        <label className={styles.control}>
          <span>Period</span>
          <select
            value={query.range}
            onChange={(event) => set('range', event.target.value as CostQuery['range'])}
            aria-label="Period"
          >
            {COST_RANGES.map((range) => (
              <option key={range} value={range}>
                {RANGE_LABELS[range]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.control}>
          <span>Granularity</span>
          <select
            value={query.granularity}
            onChange={(event) => set('granularity', event.target.value as CostQuery['granularity'])}
            aria-label="Granularity"
          >
            {COST_GRANULARITY.map((granularity) => (
              <option key={granularity} value={granularity}>
                {GRANULARITY_LABELS[granularity]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.control}>
          <span>Break down by</span>
          <select
            value={query.group_by}
            onChange={(event) => set('group_by', event.target.value as CostGrouping)}
            aria-label="Break down by"
          >
            {COST_GROUPINGS.map((grouping) => (
              <option key={grouping} value={grouping}>
                {GROUPING_LABELS[grouping]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.control}>
          <span>Show top</span>
          <select
            value={query.top}
            onChange={(event) => set('top', Number(event.target.value))}
            aria-label="Show top"
          >
            {[3, 5, 8, 12, 20].map((top) => (
              <option key={top} value={top}>
                {top}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.control}>
          <span>Chart</span>
          <select
            value={chart}
            onChange={(event) => setChart(event.target.value as CostChart)}
            aria-label="Chart"
          >
            {COST_CHARTS.map((kind) => (
              <option key={kind} value={kind}>
                {CHART_LABELS[kind]}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={query.per_venue}
            onChange={(event) => set('per_venue', event.target.checked)}
          />
          Per venue
        </label>
      </div>

      <div className={styles.filterRow}>
        {COST_GROUPINGS.filter((dimension) => dimension !== query.group_by).map((dimension) => (
          <details key={dimension} className={styles.filterMenu}>
            <summary className={styles.filterSummary}>
              {GROUPING_LABELS[dimension]}
              {(query.filters[dimension] ?? []).length > 0 ? (
                <span className={styles.filterCount}>
                  {(query.filters[dimension] ?? []).length}
                </span>
              ) : null}
            </summary>
            <ul className={styles.filterList}>
              {(data?.dimensions[dimension] ?? []).map(({ key, label }) => (
                <li key={key}>
                  <label className={styles.filterItem}>
                    <input
                      type="checkbox"
                      checked={(query.filters[dimension] ?? []).includes(key)}
                      onChange={() => toggleFilter(dimension, key)}
                    />
                    {label}
                  </label>
                </li>
              ))}
            </ul>
          </details>
        ))}
        {activeFilters.length > 0 ? (
          <span className={styles.activeFilters}>
            {activeFilters.map(([dimension, values]) => (
              <span key={dimension} className={styles.activeFilter}>
                {GROUPING_LABELS[dimension]}: {(values ?? []).join(', ')}
                <button
                  type="button"
                  onClick={() => set('filters', { ...query.filters, [dimension]: [] })}
                  aria-label={`Clear ${GROUPING_LABELS[dimension]} filter`}
                >
                  ×
                </button>
              </span>
            ))}
            <button type="button" className={styles.clearAll} onClick={clearFilters}>
              Clear all
            </button>
          </span>
        ) : null}
      </div>

      {error ? (
        <p className={styles.error}>
          Could not load costs: {(error as { message?: string }).message ?? 'unknown error'}
        </p>
      ) : null}

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>
            {RANGE_LABELS[query.range]}
            {query.per_venue ? ', per venue' : ''}
          </span>
          <span className={styles.kpiValue}>{usd(total, query.per_venue ? 2 : 0)}</span>
          <span className={styles.kpiHint}>
            <Delta current={total} previous={previous} /> vs previous{' '}
            {RANGE_LABELS[query.range].replace('Last ', '').toLowerCase()}
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Average per month</span>
          <span className={styles.kpiValue}>
            {usd(total / periodMonths, query.per_venue ? 2 : 0)}
          </span>
          <span className={styles.kpiHint}>
            over {periodMonths} month{periodMonths > 1 ? 's' : ''}
          </span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Per venue per month</span>
          <span className={styles.kpiValue}>{usd(perVenueNow / periodMonths, 2)}</span>
          <span className={styles.kpiHint}>
            {data?.venues.toLocaleString('en-GB') ?? '—'} live venues
          </span>
        </div>
        <div className={styles.kpi} data-tone="finding">
          <span className={styles.kpiLabel}>Known waste</span>
          <span className={styles.kpiValue}>
            {usd(findings.reduce((sum, finding) => sum + finding.monthly_usd, 0))}
          </span>
          <span className={styles.kpiHint}>per month across {findings.length} findings</span>
        </div>
      </div>

      <section className={styles.chartCard} aria-label="Cost chart">
        <header className={styles.chartHeader}>
          <h2 className={styles.chartTitle}>
            {GROUPING_LABELS[query.group_by]}, {GRANULARITY_LABELS[query.granularity].toLowerCase()}
            {isFetching && !isLoading ? (
              <span className={styles.refreshing}> refreshing…</span>
            ) : null}
          </h2>
          <span className={styles.chartHint}>{data?.series.length ?? 0} series</span>
        </header>
        <div className={styles.chartBody}>
          {isLoading ? (
            <p className={styles.muted}>Loading costs…</p>
          ) : (
            <AutoSize>{({ width, height }) => renderChart(width, height)}</AutoSize>
          )}
        </div>
      </section>

      <div className={styles.lower}>
        <section className={styles.breakdown} aria-label="Breakdown">
          <DataTable
            columns={columns}
            data={data?.series ?? []}
            storageKey={`nexus-ui.costs.table.${query.group_by}`}
            onRowClick={(row) => drillInto(row.key)}
            emptyMessage="No costs in this selection."
          />
        </section>
        <section className={styles.findings} aria-label="Findings">
          <h2 className={styles.findingsTitle}>Findings</h2>
          {findings.length === 0 ? (
            <p className={styles.muted}>Nothing flagged for this selection.</p>
          ) : null}
          {findings.map((finding) => (
            <article key={finding.id} className={styles.finding}>
              <div className={styles.findingTop}>
                <span className={styles.findingTitle}>{finding.title}</span>
                <span className={styles.findingValue}>{usd(finding.monthly_usd)} / mo</span>
              </div>
              <p className={styles.findingDetail}>{finding.detail}</p>
              <button
                type="button"
                className={styles.findingLink}
                onClick={() =>
                  setQuery({
                    ...query,
                    group_by: 'usage_type',
                    filters: { account: [finding.account], service: [finding.service] },
                  })
                }
              >
                Show in chart →
              </button>
            </article>
          ))}
        </section>
      </div>
    </div>
  );
};
